/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @fileoverview Sports-related React hooks
 * Provides hooks for fetching and managing sports data based on application type and device
 * @module hooks/sports-hooks
 */

import axios from "axios";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { getAuthLanguage } from "../redux/reselect/auth-selector";
import { getSports } from "../redux/slices/sportSlice";
import {
  useBetPointLineId,
  useBetPointOriginId,
  useDesktopLineId,
  useDesktopOriginId,
  useMobileLineId,
  useMobileOriginId,
  useSlipstreamLineId,
  useSlipstreamOriginId,
} from "utils/AppUtils";

/**
 * Hook that returns the appropriate origin ID based on application type and device
 * Used for API requests to identify the source application
 *
 * @function
 * @param {boolean} isSlipstream - Whether the application is Slipstream
 * @param {boolean} isBetPoint - Whether the application is BetPoint
 * @param {boolean} isMobileDevice - Whether the current device is mobile
 * @returns {string} The appropriate origin ID for the current application context
 */
const useOriginId = (isSlipstream, isBetPoint, isMobileDevice) => {
  const slipstreamOriginId = useSlipstreamOriginId();
  const betPointOriginId = useBetPointOriginId();
  const mobileOriginId = useMobileOriginId();
  const desktopOriginId = useDesktopOriginId();

  if (isSlipstream) {
    return slipstreamOriginId;
  }

  if (isBetPoint) {
    return betPointOriginId;
  }

  if (isMobileDevice) {
    return mobileOriginId;
  }

  return desktopOriginId;
};

/**
 * Hook that returns the appropriate line ID based on application type and device
 * Used for API requests to specify the betting line configuration
 *
 * @function
 * @param {boolean} isSlipstream - Whether the application is Slipstream
 * @param {boolean} isBetPoint - Whether the application is BetPoint
 * @param {boolean} isMobileDevice - Whether the current device is mobile
 * @returns {string} The appropriate line ID for the current application context
 */
const useLineId = (isSlipstream, isBetPoint, isMobileDevice) => {
  const slipstreamLineId = useSlipstreamLineId();
  const betPointLineId = useBetPointLineId();
  const mobileLineId = useMobileLineId();
  const desktopLineId = useDesktopLineId();

  if (isSlipstream) {
    return slipstreamLineId;
  }

  if (isBetPoint) {
    return betPointLineId;
  }

  if (isMobileDevice) {
    return mobileLineId;
  }

  return desktopLineId;
};

/**
 * Hook that fetches the list of available sports based on application context
 * Creates a cancelable request that automatically refreshes when language or device type changes
 *
 * @function
 * @param {boolean} isBetPoint - Whether the application is BetPoint
 * @param {boolean} isMobileDevice - Whether the current device is mobile
 * @param {boolean} isSlipstream - Whether the application is Slipstream
 * @param {Function} dispatch - Redux dispatch function
 */
export function useSports(isBetPoint, isMobileDevice, isSlipstream, dispatch) {
  const language = useSelector(getAuthLanguage);

  const originId = useOriginId(isSlipstream, isBetPoint, isMobileDevice);
  const lineId = useLineId(isSlipstream, isBetPoint, isMobileDevice);

  useEffect(() => {
    const source = axios.CancelToken.source();
    // if no config, or app type changed...

    dispatch(
      getSports({
        cancelToken: source.token,
        lineId,
        originId,
      }),
    );

    return () => {
      source.cancel();
    };
  }, [isMobileDevice, dispatch, language]);
}
